import { Component, ViewChild, OnInit, Input, forwardRef, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { MatOption } from '@angular/material/core';

export interface ChipsListDropdownItem {
  key: string;
  displayText: string;
  value: any;
  disabled?: boolean;
  disabledTooltip?: string;
  isDefault?: boolean;
}

@Component({
  selector: 'app-chips-list-dropdown',
  templateUrl: './chips-list-dropdown.component.html',
  styleUrls: ['./chips-list-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ChipsListDropdownComponent),
      multi: true,
    },
  ],
})
export class ChipsListDropdownComponent implements ControlValueAccessor, OnInit, AfterViewInit {
  @Input() placeholder: string;
  @Input() removable = true;
  @Input() data: ChipsListDropdownItem[] = [];

  @ViewChild(MatSelect, { static: true }) matSelect: MatSelect;
  @ViewChild('allSelected', { static: true }) allSelectedOption: MatOption;
  private _value: ChipsListDropdownItem[] = [];
  private _allSelected = false;

  get selectedItems(): ChipsListDropdownItem[] {
    return this._value;
  }

  set selectedItems(value: ChipsListDropdownItem[]) {
    this._value = value;
    this.onChange(value);
  }

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit() {
    this.matSelect.valueChange.subscribe((value) => {
      this.selectedItems = this.data.filter((x) => value.find((y) => y === x.key));
      this._allSelected = this.selectedItems.length === this.data.length;
    });

    this.allSelectedOption.onSelectionChange.subscribe((value) => {
      if (value.source.selected) {
        this.selectAll();
      } else if (value.isUserInput) {
        this.unselectAll();
      }
    });
  }

  ngAfterViewInit() {
    // Defer to next tick so mat-options are fully initialized in Material 15
    setTimeout(() => {
      this.selectItems(this.data.filter((x) => !!x.isDefault));
      // Ensure mat-select value is synced from writeValue if no defaults matched
      if (this._value.length > 0 && (!this.matSelect.value || this.matSelect.value.length === 0)) {
        this.matSelect.value = this._value.map((x) => x.key);
      }
      this.cdr.detectChanges();
    });
  }

  writeValue(obj: any): void {
    if (!!obj) {
      this._value = Array.isArray(obj) ? obj : [obj];
      // Sync matSelect value after options are initialized
      Promise.resolve().then(() => {
        if (this.matSelect && this._value.length > 0) {
          const keys = this._value.map((x) => x.key);
          if (!this.matSelect.value || this.matSelect.value.length === 0) {
            this.matSelect.value = keys;
          }
          this.cdr.markForCheck();
        }
      });
    }
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {}
  setDisabledState?(isDisabled: boolean): void {}

  private onChange = (value: ChipsListDropdownItem[]) => {};

  private selectAll() {
    const selectableItems = this.data.filter((x) => !x.disabled);
    this.matSelect.value = selectableItems.map((x) => x.key);
    this.selectedItems = selectableItems;
  }

  private unselectAll() {
    this.matSelect.value = [];
    this.selectedItems = [];
  }

  selectItems(items: ChipsListDropdownItem[]) {
    const selectedDropdownItems = this.matSelect.value || [];
    this.matSelect.value = [...items.map((x) => x.key), ...selectedDropdownItems];

    const selectedChipItems = this.selectedItems || [];
    this.selectedItems = [
      ...items.filter((x) => !selectedChipItems.find((y) => y.key === x.key)),
      ...selectedChipItems,
    ];
  }

  remove(item: ChipsListDropdownItem): void {
    this.selectedItems = this.selectedItems.filter((x) => x.value !== item.value);
    this.matSelect.value = this.matSelect.value.filter((x) => x !== item.key);
  }
}
